import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { TeamMember } from '../types/team';

const Team = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedMember, setEditedMember] = useState<TeamMember | null>(null);
  const [isAddingResumeLink, setIsAddingResumeLink] = useState(false);
  const [resumeLink, setResumeLink] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Simulate loading data from backend
    const timer = setTimeout(() => {
      setMembers(dummyTeamMembers);
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleMemberClick = (member: TeamMember) => {
    setSelectedMember(member);
    setEditedMember(member);
    setResumeLink(member.resume || '');
    setIsAddingResumeLink(false);
  };

  const handleClosePopup = () => {
    setSelectedMember(null);
    setIsEditing(false);
    setIsAddingResumeLink(false);
    setResumeLink('');
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setIsAddingResumeLink(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof TeamMember) => {
    if (!editedMember) return;

    setEditedMember({
      ...editedMember,
      [field]: e.target.value
    });
  };

  const handleArrayInputChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'skills' | 'strengths') => {
    if (!editedMember) return;

    setEditedMember({
      ...editedMember,
      [field]: e.target.value.split(',').map(item => item.trim())
    });
  };

  const handleResumeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editedMember || !e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const resumeUrl = URL.createObjectURL(file);

    setEditedMember({
      ...editedMember,
      resume: resumeUrl
    });
    setResumeLink(resumeUrl);
    setIsAddingResumeLink(false);
  };

  const triggerResumeUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const toggleAddResumeLink = () => {
    setIsAddingResumeLink(!isAddingResumeLink);
  };

  const handleResumeLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResumeLink(e.target.value);
  };

  const saveResumeLink = () => {
    if (!editedMember || !resumeLink) return;

    setEditedMember({
      ...editedMember,
      resume: resumeLink
    });
    setIsAddingResumeLink(false);
  };

  const handleSaveChanges = () => {
    if (!editedMember) return;

    // Check if this is a new member or an existing one
    const isNewMember = !members.some(m => m.id === editedMember.id);

    if (isNewMember) {
      setMembers([...members, editedMember]);
    } else {
      setMembers(members.map(member =>
        member.id === editedMember.id ? editedMember : member
      ));
    }

    setSelectedMember(editedMember);
    setIsEditing(false);
    setIsAddingResumeLink(false);
  };

  const handleAddNewMember = () => {
    const newMember: TeamMember = {
      id: `${Date.now()}`,
      name: '',
      role: '',
      email: '',
      phone: '',
      skills: [],
      strengths: [],
      jobDescription: '',
      notes: ''
    };

    setEditedMember(newMember);
    setSelectedMember(newMember);
    setIsEditing(true);
    setResumeLink('');
    setIsAddingResumeLink(false);
  };

  const handleDeleteMember = () => {
    if (!selectedMember) return;

    setMembers(members.filter(member => member.id !== selectedMember.id));
    setSelectedMember(null);
    setIsEditing(false);
    setIsAddingResumeLink(false);
  };

  return (
    <div className="min-h-screen bg-secondary text-white relative">
      {/* Background Effect */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <div className="absolute -top-1/2 -left-1/4 w-3/4 h-3/4 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/2 -right-1/4 w-3/4 h-3/4 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/3 w-1/3 h-1/3 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <header className="bg-primary/80 backdrop-blur-md px-6 py-4 border-b border-border-color sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold text-white">Team Management</h1>
          <button
            onClick={() => navigate('/chat')}
            className="text-sm px-4 py-2 rounded-md bg-accent hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
          >
            Back to Chat
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 relative z-1">
        <div className="bg-primary/90 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/5 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-accent/5 opacity-50"></div>
          <div className="p-8 relative">
            <div className="mb-6 pb-6 border-b border-border-color flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-extrabold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  Team Members
                </h2>
                <p className="text-gray-400">Manage your team profile information</p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleAddNewMember}
                  className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white text-sm font-medium shadow-lg shadow-accent/20"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add Member
                </button>
                <div className="bg-accent/20 p-3 rounded-full shadow-lg shadow-accent/10">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-accent">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                  </svg>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {members.map(member => (
                  <div
                    key={member.id}
                    onClick={() => handleMemberClick(member)}
                    className="p-4 bg-tertiary rounded-xl hover:bg-gray-700 transition-colors cursor-pointer border border-border-color shadow-md"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden border-2 border-accent">
                        {member.photo ? (
                          <img src={member.photo} alt={member.name} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-lg font-semibold">
                            {member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{member.name}</h3>
                        <p className="text-gray-400 text-sm">{member.role}</p>
                      </div>
                      <div>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Member Detail Popup */}
      <AnimatePresence>
        {selectedMember && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-primary rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar border border-border-color"
            >
              <div className="sticky top-0 z-10 bg-primary p-6 border-b border-border-color flex justify-between items-center backdrop-blur-md bg-opacity-95">
                <h2 className="text-xl font-bold text-white">
                  {isEditing ? "Edit Member Profile" : "Member Profile"}
                </h2>
                <div className="flex gap-2">
                  {!isEditing && (
                    <>
                      <button
                        onClick={handleEditToggle}
                        className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                        </svg>
                      </button>
                      <button
                        onClick={handleDeleteMember}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleClosePopup}
                    className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {isEditing ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                        <input
                          type="text"
                          value={editedMember?.name || ''}
                          onChange={(e) => handleInputChange(e, 'name')}
                          className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Role</label>
                        <input
                          type="text"
                          value={editedMember?.role || ''}
                          onChange={(e) => handleInputChange(e, 'role')}
                          className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                        <input
                          type="email"
                          value={editedMember?.email || ''}
                          onChange={(e) => handleInputChange(e, 'email')}
                          className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Phone</label>
                        <input
                          type="text"
                          value={editedMember?.phone || ''}
                          onChange={(e) => handleInputChange(e, 'phone')}
                          className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Skills (comma-separated)</label>
                      <input
                        type="text"
                        value={editedMember?.skills.join(', ') || ''}
                        onChange={(e) => handleArrayInputChange(e, 'skills')}
                        className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Strengths (comma-separated)</label>
                      <input
                        type="text"
                        value={editedMember?.strengths.join(', ') || ''}
                        onChange={(e) => handleArrayInputChange(e, 'strengths')}
                        className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Job Description</label>
                      <textarea
                        value={editedMember?.jobDescription || ''}
                        onChange={(e) => handleInputChange(e, 'jobDescription')}
                        rows={4}
                        className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Resume</label>
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleResumeUpload}
                            className="hidden"
                          />
                          <button
                            onClick={triggerResumeUpload}
                            className="px-4 py-2 bg-tertiary hover:bg-gray-700 rounded-lg transition-colors border border-border-color text-sm flex items-center gap-2"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                            </svg>
                            Upload Resume File
                          </button>
                          <span className="text-gray-400">or</span>
                          <button
                            onClick={toggleAddResumeLink}
                            className="px-4 py-2 bg-tertiary hover:bg-gray-700 rounded-lg transition-colors border border-border-color text-sm flex items-center gap-2"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                            </svg>
                            Add Resume Link
                          </button>
                          {editedMember?.resume && !isAddingResumeLink && (
                            <span className="text-green-400 text-sm">Resume {resumeLink.startsWith('blob:') ? 'file' : 'link'} added</span>
                          )}
                        </div>

                        {isAddingResumeLink && (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={resumeLink}
                              onChange={handleResumeLinkChange}
                              placeholder="Enter resume URL (https://...)"
                              className="flex-1 bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                            />
                            <button
                              onClick={saveResumeLink}
                              className="px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors text-white"
                            >
                              Save
                            </button>
                            <button
                              onClick={toggleAddResumeLink}
                              className="p-2 rounded-lg hover:bg-tertiary transition-colors"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Notes</label>
                      <textarea
                        value={editedMember?.notes || ''}
                        onChange={(e) => handleInputChange(e, 'notes')}
                        rows={3}
                        className="w-full bg-tertiary rounded-lg border border-border-color p-2.5 text-white"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        onClick={handleClosePopup}
                        className="px-4 py-2 border border-border-color rounded-lg hover:bg-tertiary transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        className="px-4 py-2 bg-accent hover:bg-accent/90 rounded-lg transition-colors shadow-md shadow-accent/20"
                      >
                        {selectedMember?.id && members.some(m => m.id === selectedMember.id) ? 'Save Changes' : 'Add Member'}
                      </button>
                    </div>
                  </div>
                ) : (
                  selectedMember && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="h-20 w-20 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden border-2 border-accent">
                          {selectedMember.photo ? (
                            <img src={selectedMember.photo} alt={selectedMember.name} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-2xl font-semibold">
                              {selectedMember.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          )}
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold">{selectedMember.name}</h3>
                          <p className="text-accent">{selectedMember.role}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Contact Info</h4>
                          <div className="space-y-2">
                            <p className="flex items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                              </svg>
                              <span>{selectedMember.email}</span>
                            </p>
                            {selectedMember.phone && (
                              <p className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-400">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                </svg>
                                <span>{selectedMember.phone}</span>
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Resume</h4>
                          <div className="p-3 bg-tertiary rounded-lg border border-border-color">
                            {selectedMember.resume ? (
                              <a
                                href={selectedMember.resume}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-accent hover:text-accent/80 transition-colors"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9.75M8.25 3v1.5M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                                </svg>
                                View Resume {selectedMember.resume.startsWith('blob:') ? '(Uploaded File)' : '(External Link)'}
                              </a>
                            ) : (
                              <p className="text-gray-400 text-sm">No resume available</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Job Description</h4>
                        <div className="p-3 bg-tertiary rounded-lg border border-border-color">
                          <p className="text-sm text-gray-300">{selectedMember.jobDescription}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Skills</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedMember.skills.map((skill, index) => (
                              <span key={index} className="px-3 py-1 bg-accent/20 text-accent rounded-full text-xs">
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Strengths</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedMember.strengths.map((strength, index) => (
                              <span key={index} className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                                {strength}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>

                      {selectedMember.notes && (
                        <div>
                          <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2">Notes</h4>
                          <div className="p-3 bg-tertiary rounded-lg border border-border-color">
                            <p className="text-sm text-gray-300">{selectedMember.notes}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Dummy team members data
const dummyTeamMembers: TeamMember[] = [
  {
    id: '1',
    name: 'Alex Johnson',
    role: 'Product Manager',
    email: 'alex.johnson@example.com',
    phone: '+1 (555) 123-4567',
    skills: ['Product Strategy', 'Agile', 'User Research', 'Roadmapping', 'Data Analysis'],
    strengths: ['Strategic Vision', 'Cross-functional Leadership', 'Problem Solving'],
    jobDescription: 'Responsible for defining the product vision and strategy. Works with engineering, design, and marketing to deliver features that meet customer needs and business objectives.',
    resume: 'https://example.com/resumes/alex-johnson-resume.pdf',
    notes: 'Prefers morning meetings and likes to have agenda shared at least 24 hours in advance.'
  },
  {
    id: '2',
    name: 'Sarah Chen',
    role: 'Senior Developer',
    email: 'sarah.chen@example.com',
    skills: ['JavaScript', 'React', 'Node.js', 'TypeScript', 'GraphQL'],
    strengths: ['Technical Architecture', 'Code Quality', 'Mentoring'],
    jobDescription: 'Develops and maintains frontend applications. Collaborates with backend developers to implement APIs and optimize performance. Mentors junior team members.',
    resume: 'https://example.com/resumes/sarah-chen-resume.pdf'
  },
  {
    id: '3',
    name: 'Michael Rodriguez',
    role: 'UX Designer',
    email: 'michael.rodriguez@example.com',
    phone: '+1 (555) 987-6543',
    skills: ['User Research', 'Wireframing', 'Prototyping', 'Design Systems', 'Figma'],
    strengths: ['Visual Design', 'User Empathy', 'Communication'],
    jobDescription: 'Creates user-centered designs and prototypes. Conducts user research and usability testing. Collaborates with product and engineering teams to deliver intuitive interfaces.',
    notes: 'Very visual thinker - prefers sketching ideas rather than writing them out.'
  },
  {
    id: '4',
    name: 'Diana Williams',
    role: 'Data Scientist',
    email: 'diana.williams@example.com',
    skills: ['Python', 'Machine Learning', 'Data Visualization', 'SQL', 'Statistics'],
    strengths: ['Pattern Recognition', 'Analytical Thinking', 'Research'],
    jobDescription: 'Analyzes complex data sets to extract insights. Builds predictive models and algorithms. Communicates findings to stakeholders to drive data-informed decisions.',
    resume: 'https://example.com/resumes/diana-williams-resume.pdf'
  },
  {
    id: '5',
    name: 'James Wilson',
    role: 'Project Manager',
    email: 'james.wilson@example.com',
    phone: '+1 (555) 234-5678',
    skills: ['Project Planning', 'Risk Management', 'Scrum', 'Budgeting', 'Stakeholder Management'],
    strengths: ['Organization', 'Communication', 'Adaptability'],
    jobDescription: 'Oversees project planning, execution, and delivery. Manages resources, timelines, and scope. Facilitates communication between team members and stakeholders.',
    notes: 'Prefers structured updates in team meetings with clear action items and owners.'
  }
];

export default Team;
